import { gql } from '@apollo/client'

export const CONTENT_SEARCH_TYPEAHEAD_QUERY = gql`
  query TranscriptChunkSearchTypeahead(
    $where: ContentWhereInput!
    $language: [String]!
    $limit: Int
  ) {
    contentList(where: $where, language: $language, limit: $limit) {
      content {
        contentId
        title
        contentType
        bundle
      }
    }
  }
`

export const TRANSCRIPT_CHUNK_LOOKUP_QUERY = gql`
  query TranscriptChunkLookup($input: TranscriptChunkSearchInput!) {
    transcriptChunkSearch(input: $input) {
      resultId
      language
      spans {
        contentRowId
        featureMediaId
        startTimecode
        endTimecode
        transcriptText
        matchedText
        relevanceScore
        content {
          contentId
          title
          path
          episodeNumber
          seasonNumber
          contentSeries {
            title
          }
        }
        featureMedia {
          isGaiaOriginal
        }
      }
    }
  }
`

export type ChunkLookupSpan = {
  contentRowId: string
  featureMediaId?: string | null
  startTimecode: string
  endTimecode: string
  transcriptText?: string | null
  matchedText?: string | null
  relevanceScore?: number | null
  content?: {
    contentId?: number | null
    title?: string | null
    path?: string | null
    episodeNumber?: number | null
    seasonNumber?: number | null
    contentSeries?: Array<{ title?: string | null } | null> | null
  } | null
  featureMedia?: {
    isGaiaOriginal?: boolean | null
  } | null
}

export type ChunkLookupResult = {
  resultId: string
  language: string
  spans: ChunkLookupSpan[]
}

export const TRANSCRIPT_CHUNK_LOOKUP_PROJECT_GET = gql`
  query TranscriptChunkLookupProject($id: ID!) {
    transcriptChunkLookupProject(id: $id) {
      id
      name
      clips {
        id
        contentRowId
        featureMediaId
        lemonadeContentId
        startTimecode
        endTimecode
        originalStartTimecode
        originalEndTimecode
        transcriptText
        matchedText
        contentTitle
        seriesTitle
        contentPath
        isLicensed
        searchLanguage
        addedAt
      }
      lastSearchContext {
        prompt
        language
      }
      rawExportStatus
      rawExportReadyAt
      rawExportExpiresAt
      rawExportRequestedAt
      createdAt
      updatedAt
    }
  }
`

export const TRANSCRIPT_CHUNK_LOOKUP_PROJECT_LIST = gql`
  query TranscriptChunkLookupProjectList($limit: Int) {
    transcriptChunkLookupProjectList(limit: $limit) {
      id
      name
      clips {
        id
        contentRowId
        featureMediaId
        lemonadeContentId
        startTimecode
        endTimecode
        originalStartTimecode
        originalEndTimecode
        transcriptText
        matchedText
        contentTitle
        seriesTitle
        contentPath
        isLicensed
        searchLanguage
        addedAt
      }
      lastSearchContext {
        prompt
        language
      }
      rawExportStatus
      rawExportReadyAt
      rawExportExpiresAt
      rawExportRequestedAt
      createdAt
      updatedAt
    }
  }
`

export const TRANSCRIPT_CHUNK_LOOKUP_PROJECT_CREATE = gql`
  mutation TranscriptChunkLookupProjectCreate($input: TranscriptChunkLookupProjectCreateInput!) {
    transcriptChunkLookupProjectCreate(input: $input) {
      id
      name
      clips {
        id
        contentRowId
        featureMediaId
        lemonadeContentId
        startTimecode
        endTimecode
        originalStartTimecode
        originalEndTimecode
        transcriptText
        matchedText
        contentTitle
        seriesTitle
        contentPath
        isLicensed
        searchLanguage
        addedAt
      }
      lastSearchContext {
        prompt
        language
      }
      rawExportStatus
      rawExportReadyAt
      rawExportExpiresAt
      rawExportRequestedAt
      createdAt
      updatedAt
    }
  }
`

export const TRANSCRIPT_CHUNK_LOOKUP_PROJECT_UPDATE = gql`
  mutation TranscriptChunkLookupProjectUpdate(
    $id: ID!
    $input: TranscriptChunkLookupProjectUpdateInput!
  ) {
    transcriptChunkLookupProjectUpdate(id: $id, input: $input) {
      id
      name
      clips {
        id
        contentRowId
        featureMediaId
        lemonadeContentId
        startTimecode
        endTimecode
        originalStartTimecode
        originalEndTimecode
        transcriptText
        matchedText
        contentTitle
        seriesTitle
        contentPath
        isLicensed
        searchLanguage
        addedAt
      }
      lastSearchContext {
        prompt
        language
      }
      rawExportStatus
      rawExportReadyAt
      rawExportExpiresAt
      rawExportRequestedAt
      createdAt
      updatedAt
    }
  }
`

export const TRANSCRIPT_CHUNK_LOOKUP_PROJECT_DELETE = gql`
  mutation TranscriptChunkLookupProjectDelete($id: ID!) {
    transcriptChunkLookupProjectDelete(id: $id)
  }
`

export const TRANSCRIPT_CHUNK_LOOKUP_PROJECT_REQUEST_RAW_EXPORT = gql`
  mutation TranscriptChunkLookupProjectRequestRawExport($projectId: ID!, $force: Boolean) {
    transcriptChunkLookupProjectRequestRawExport(projectId: $projectId, force: $force) {
      id
      rawExportStatus
      rawExportReadyAt
      rawExportExpiresAt
      rawExportRequestedAt
    }
  }
`

export const TRANSCRIPT_CHUNK_LOOKUP_PROJECT_RAW_EXPORT_BUNDLE = gql`
  query TranscriptChunkLookupProjectRawExportBundle($projectId: ID!) {
    transcriptChunkLookupProjectRawExportBundle(projectId: $projectId) {
      presignExpiresAt
      project {
        id
        name
        clips {
          id
          contentRowId
          featureMediaId
          lemonadeContentId
          startTimecode
          endTimecode
          originalStartTimecode
          originalEndTimecode
          transcriptText
          matchedText
          contentTitle
          seriesTitle
          contentPath
          isLicensed
          addedAt
        }
        lastSearchContext {
          prompt
          language
        }
        rawExportStatus
        rawExportReadyAt
        rawExportExpiresAt
      }
      sources {
        featureMediaId
        contentId
        s3Uri
        fileName
        availability
        downloadUrl
        downloadUrlExpiresAt
      }
    }
  }
`

export type RawExportStatus = 'PENDING_RESTORE' | 'READY' | 'EXPIRED' | 'FAILED' | null

export type GraphQLProjectClip = {
  id: string
  contentRowId: string
  featureMediaId: string
  lemonadeContentId?: number | null
  startTimecode: string
  endTimecode: string
  originalStartTimecode: string
  originalEndTimecode: string
  transcriptText?: string | null
  matchedText?: string | null
  contentTitle?: string | null
  seriesTitle?: string | null
  contentPath?: string | null
  isLicensed?: boolean | null
  searchLanguage?: string | null
  addedAt: string
}

export type GraphQLLookupProject = {
  id: string
  name: string
  clips: GraphQLProjectClip[]
  lastSearchContext?: { prompt?: string | null; language?: string | null } | null
  rawExportStatus?: RawExportStatus
  rawExportReadyAt?: string | null
  rawExportExpiresAt?: string | null
  rawExportRequestedAt?: string | null
  createdAt?: string | null
  updatedAt?: string | null
}

export type RawExportBundleSource = {
  featureMediaId: string
  contentId?: string | null
  s3Uri?: string | null
  fileName?: string | null
  availability: string
  downloadUrl?: string | null
  downloadUrlExpiresAt?: string | null
}

export type RawExportBundle = {
  presignExpiresAt: string
  project: GraphQLLookupProject
  sources: RawExportBundleSource[]
}
